import { createSlice } from "@reduxjs/toolkit";

import type { IPlayer, IQueueEvent } from "../../types";

export interface ILeaderRecord {
  value: number;
  playerName: string;
  event_uuid: string;
}

export const initialState = {
  teamName: "" as string,
  roster: [] as Array<IPlayer>,
  currentHitter: {} as IPlayer,
  isBattingPracticeActive: false as boolean,
  exitVeloLeaders: [] as ILeaderRecord[],
  distanceLeaders: [] as ILeaderRecord[],
};

// For updating leaderboard record arrays
function updateRecordArray(
  arr: ILeaderRecord[],
  newObj: ILeaderRecord,
): ILeaderRecord[] {
  if (arr.some((obj) => obj.event_uuid === newObj.event_uuid)) {
    return arr;
  }
  if (arr.length < 3) {
    return [...arr, newObj].sort((a, b) => b.value - a.value);
  } else {
    const smallestObj = arr.reduce((acc, cur) =>
      acc.value < cur.value ? acc : cur,
    );
    if (newObj.value > smallestObj.value) {
      const idx = arr.indexOf(smallestObj);
      const newArr = [...arr];
      newArr[idx] = newObj;
      return newArr.sort((a, b) => b.value - a.value);
    } else {
      return arr.sort((a, b) => b.value - a.value);
    }
  }
}

const battingPracticeSlice = createSlice({
  name: "battingPractice",
  initialState,
  reducers: {
    setCurrentBattingPracticeHitter: (
      state,
      action: { payload: { player: IPlayer } },
    ) => {
      state.currentHitter = action.payload.player;
    },
    setBattingPracticeRoster: (state, action) => {
      state.roster = action.payload;
    },
    addToBattingPracticeRoster: (
      state,
      action: { payload: { player: IPlayer } },
    ) => {
      state.roster.push(action.payload.player);
    },
    removeFromBattingPracticeRoster: (state, action) => {
      state.roster = state.roster.filter(
        (elem) => elem.uuid !== action.payload.uuid,
      );
    },
    setBattingPracticeTeamName: (state, action) => {
      state.teamName = action.payload;
    },
    setBattingPracticeActive: (state, action) => {
      state.isBattingPracticeActive = action.payload;
    },
    clearBattingPracticeLeaders: (state) => {
      return (state = {
        ...state,
        exitVeloLeaders: [],
        distanceLeaders: [],
      });
    },
    clearBattingPracticeRoster: (state) => {
      state = {
        roster: [],
        teamName: "",
        currentHitter: {} as IPlayer,
        isBattingPracticeActive: false,
        exitVeloLeaders: [],
        distanceLeaders: [],
      };
      return state;
    },
    updateBattingPracticeLeaders: (
      state,
      action: { payload: { event: IQueueEvent; player: IPlayer } },
    ) => {
      const { event, player } = action.payload;
      if (event.hit_data && player) {
        const newVeloRecord = {
          value: event.hit_data.ExitSpeedMPH,
          playerName: `${player.firstName} ${player.lastName}`,
          event_uuid: event.event_uuid,
        };
        const newDistanceRecord = {
          value: event.hit_data.DistanceFeet,
          playerName: `${player.firstName} ${player.lastName}`,
          event_uuid: event.event_uuid,
        };
        state.exitVeloLeaders = updateRecordArray(
          state.exitVeloLeaders,
          newVeloRecord,
        );
        state.distanceLeaders = updateRecordArray(
          state.distanceLeaders,
          newDistanceRecord,
        );
      }
    },
  },
});

export const {
  setCurrentBattingPracticeHitter,
  clearBattingPracticeRoster,
  setBattingPracticeRoster,
  addToBattingPracticeRoster,
  removeFromBattingPracticeRoster,
  setBattingPracticeTeamName,
  setBattingPracticeActive,
  clearBattingPracticeLeaders,
  updateBattingPracticeLeaders,
} = battingPracticeSlice.actions;
export default battingPracticeSlice.reducer;
